import type { SxProps, Theme } from '@mui/material'
import { Box, useTheme } from '@mui/material'
import { interpolate } from 'flubber'
import React, { useCallback, useEffect, useRef, useState } from 'react'

export interface MorphingLoaderProps {
  /** Size of the loader in pixels */
  size?: number
  /** Duration of each shape transition in milliseconds */
  transitionDuration?: number
  /** Degrees to rotate per morph burst */
  rotationPerMorph?: number
  /** Color of the shape - defaults to primary color from theme */
  color?: string
  /** Additional styles */
  sx?: SxProps<Theme>
}

/**
 * Constant for approximating a circle with cubic Bézier curves.
 * Derived from (4/3) * tan(π/8) ≈ 0.5522847498
 * @see https://spencermortensen.com/articles/bezier-circle/
 */
const BEZIER_CIRCLE_CONSTANT = 0.552284749831

/** Padding from edge of viewBox to shape boundary in pixels */
const SHAPE_PADDING = 2

/** Corner radius as a ratio of the shape size for rounded squares */
const CORNER_RADIUS_RATIO = 0.25

/** Height of pill shape as a ratio of the total size */
const PILL_HEIGHT_RATIO = 0.5

// SVG path generators for each shape
const createCirclePath = (size: number): string => {
  const cx = size / 2
  const cy = size / 2
  const r = size / 2 - SHAPE_PADDING
  const c = r * BEZIER_CIRCLE_CONSTANT
  return `
    M ${cx} ${cy - r}
    C ${cx + c} ${cy - r}, ${cx + r} ${cy - c}, ${cx + r} ${cy}
    C ${cx + r} ${cy + c}, ${cx + c} ${cy + r}, ${cx} ${cy + r}
    C ${cx - c} ${cy + r}, ${cx - r} ${cy + c}, ${cx - r} ${cy}
    C ${cx - r} ${cy - c}, ${cx - c} ${cy - r}, ${cx} ${cy - r}
    Z
  `.trim()
}

const createRoundedSquarePath = (size: number): string => {
  const cornerRadius = size * CORNER_RADIUS_RATIO
  const left = SHAPE_PADDING
  const right = size - SHAPE_PADDING
  const top = SHAPE_PADDING
  const bottom = size - SHAPE_PADDING

  return `
    M ${left + cornerRadius} ${top}
    L ${right - cornerRadius} ${top}
    Q ${right} ${top}, ${right} ${top + cornerRadius}
    L ${right} ${bottom - cornerRadius}
    Q ${right} ${bottom}, ${right - cornerRadius} ${bottom}
    L ${left + cornerRadius} ${bottom}
    Q ${left} ${bottom}, ${left} ${bottom - cornerRadius}
    L ${left} ${top + cornerRadius}
    Q ${left} ${top}, ${left + cornerRadius} ${top}
    Z
  `.trim()
}

const createPillPath = (size: number): string => {
  const height = size * PILL_HEIGHT_RATIO
  const top = (size - height) / 2
  const bottom = top + height
  const left = SHAPE_PADDING
  const right = size - SHAPE_PADDING
  const radius = height / 2

  return `
    M ${left + radius} ${top}
    L ${right - radius} ${top}
    A ${radius} ${radius} 0 0 1 ${right - radius} ${bottom}
    L ${left + radius} ${bottom}
    A ${radius} ${radius} 0 0 1 ${left + radius} ${top}
    Z
  `.trim()
}

type ShapeType = 'circle' | 'roundedSquare' | 'pill'

const SHAPES: ShapeType[] = ['circle', 'roundedSquare', 'pill']

/** Ratio of pause duration relative to transition duration between morphs */
const MORPH_PAUSE_RATIO = 0.3

const MorphingLoader: React.FC<MorphingLoaderProps> = ({
  size = 48,
  transitionDuration = 300,
  rotationPerMorph = 80,
  color,
  sx,
}) => {
  const theme = useTheme()
  const pathRef = useRef<SVGPathElement>(null)
  const svgRef = useRef<SVGSVGElement>(null)
  const animationRef = useRef<number | null>(null)
  const rotationRef = useRef(0)
  const [currentShapeIndex, setCurrentShapeIndex] = useState(0)

  const shapeColor = color ?? theme.palette.primary.main

  // Generate paths for all shapes
  const paths = React.useMemo(
    () => ({
      circle: createCirclePath(size),
      roundedSquare: createRoundedSquarePath(size),
      pill: createPillPath(size),
    }),
    [size],
  )

  const animateMorph = useCallback(
    (fromPath: string, toPath: string, onComplete: () => void) => {
      const interpolator = interpolate(fromPath, toPath, { maxSegmentLength: 2 })
      const startTime = performance.now()
      const startRotation = rotationRef.current
      const targetRotation = startRotation + rotationPerMorph

      const animate = (currentTime: number) => {
        const elapsed = currentTime - startTime
        const progress = Math.min(elapsed / transitionDuration, 1)

        // Easing function (ease-out cubic) - fast start, slow end for snappy feel
        const eased = 1 - Math.pow(1 - progress, 3)

        if (pathRef.current) {
          pathRef.current.setAttribute('d', interpolator(eased))
        }

        // Animate rotation in sync with morph
        if (svgRef.current) {
          const currentRotation = startRotation + (targetRotation - startRotation) * eased
          rotationRef.current = currentRotation
          svgRef.current.style.transform = `rotate(${currentRotation}deg)`
        }

        if (progress < 1) {
          animationRef.current = requestAnimationFrame(animate)
        } else {
          rotationRef.current = targetRotation
          onComplete()
        }
      }

      animationRef.current = requestAnimationFrame(animate)
    },
    [transitionDuration, rotationPerMorph],
  )

  useEffect(() => {
    // Initialize with first shape
    if (pathRef.current) {
      pathRef.current.setAttribute('d', paths[SHAPES[0]])
    }
  }, [paths])

  useEffect(() => {
    const morphToNextShape = () => {
      const nextIndex = (currentShapeIndex + 1) % SHAPES.length
      const fromPath = paths[SHAPES[currentShapeIndex]]
      const toPath = paths[SHAPES[nextIndex]]

      animateMorph(fromPath, toPath, () => {
        setCurrentShapeIndex(nextIndex)
      })
    }

    // Start the morph after a brief pause on each shape
    const pauseDuration = transitionDuration * MORPH_PAUSE_RATIO
    const timeoutId = setTimeout(morphToNextShape, pauseDuration)

    return () => {
      clearTimeout(timeoutId)
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }
  }, [currentShapeIndex, paths, animateMorph, transitionDuration])

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: size,
        height: size,
        ...sx,
      }}
    >
      <svg
        ref={svgRef}
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        style={{
          transform: 'rotate(0deg)',
        }}
      >
        <path ref={pathRef} fill={shapeColor} />
      </svg>
    </Box>
  )
}

export default MorphingLoader
